import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import EditIcon from '@mui/icons-material/Edit';

import { PhraseHighlight } from '@/components/displays/PhraseHighlight';
import { ImagePicker } from '@/components/inputs/ImagePicker/ImagePicker';
import { BUILTIN_IMG_SRC } from '@/components/inputs/ImagePicker/source';
import { WordPicker } from '@/components/inputs/WordPicker/WordPicker';
import { Popover } from '@/components/utils/Popover/Popover';
import { CANVAS_SIZE } from '@/constant';
import { SlideComponentHkcsQ1 } from '@/types/slide';
import { Box, Button, Divider, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, Stack, TextField, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'rooks';
import { ImageSelector } from '../components/ImageSelector';
import { Pronunciation } from '../components/Pronunciation';

interface Props {
    anchorEl: HTMLElement | null;
    value: SlideComponentHkcsQ1;
    slotProps?: { paper?: { ref: React.Ref<HTMLDivElement> } };
    onClose: () => void;
    onUpdate?: (model: SlideComponentHkcsQ1) => void;
    onUploadImage?: (file: File) => Promise<string>;
}

export const EditPopover: React.FC<Props> = ({ anchorEl, value, slotProps, onClose, onUpdate, onUploadImage }) => {
    const { t } = useTranslation();
    const open = Boolean(anchorEl);

    // ref
    const wordPickerRef = useRef<HTMLDivElement | null>(null);
    const wordSelectorRef = useRef<HTMLElement | null>(null);
    const imagePickerRef = useRef<HTMLDivElement | null>(null);

    // ui state
    const [wordPickerAnchorEl, setWordPickerAnchorEl] = useState<HTMLElement | null>(null);
    const [imagePickerAnchorEl, setImagePickerAnchorEl] = useState<HTMLElement | null>(null);
    const [imagePickerSource, setImagePickerSource] = useState<'primary' | 'secondary' | number | null>(null);

    const debouncedUpdate = useDebounce(onUpdate || (() => {}), 500);

    return (
        <>
            <Popover
                title={t('slide.legacy.hkcsQ1.title')}
                open={open}
                anchorEl={anchorEl}
                onClose={() => {
                    onClose();
                }}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                }}
                slotProps={{
                    paper: {
                        ref: slotProps?.paper?.ref,
                        sx: {
                            display: 'flex',
                            p: 2,
                            width: CANVAS_SIZE.width,
                            height: CANVAS_SIZE.height,
                            overflow: 'auto',
                        },
                    },
                }}
            >
                <Box sx={{ display: 'flex', flex: 1, flexDirection: 'row', width: CANVAS_SIZE.width - 32 }}>
                    <Box sx={{ width: '70%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Stack spacing={1} sx={{ alignItems: 'center' }}>
                            <Box
                                sx={(theme) => ({
                                    padding: 1,
                                    borderRadius: 1,
                                    backgroundColor: (theme.vars || theme).palette.grey[200],
                                    ...theme.applyStyles('dark', {
                                        backgroundColor: (theme.vars || theme).palette.grey[600],
                                    }),
                                })}
                                ref={wordSelectorRef}
                            >
                                <Box
                                    sx={(theme) => ({
                                        minWidth: 240,
                                        minHeight: 106,
                                        borderRadius: 1,
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'center',
                                        gap: 1,
                                        alignItems: 'center',
                                        p: 1,
                                        position: 'relative',
                                        backgroundColor: (theme.vars || theme).palette.grey[100],
                                        ...theme.applyStyles('dark', {
                                            backgroundColor: (theme.vars || theme).palette.grey[700],
                                        }),
                                    })}
                                >
                                    {value.content.phrase ? (
                                        <>
                                            <Paper sx={{ p: 1 }}>
                                                <Divider textAlign='left'>
                                                    <Typography variant='caption'>{t('slide.legacy.character.choosePronunciation')}</Typography>
                                                </Divider>
                                                {value.content?.phraseSoundGroup && (
                                                    <Box sx={{ mt: 1.5 }}>
                                                        <FormControl fullWidth>
                                                            <InputLabel id='cantonese-pronunciation-select-label'>{t('common.cantonese')}</InputLabel>
                                                            <Select
                                                                labelId='cantonese-pronunciation-select-label'
                                                                id='cantonese-pronunciation-select-id'
                                                                value={
                                                                    value.content?.phraseSoundGroup?.jyutping && value.content?.phraseSoundGroup.jyutping[0]
                                                                        ? value.content?.phraseSoundGroup.jyutping[0].startIndex.toString()
                                                                        : ''
                                                                }
                                                                label={t('common.cantonese')}
                                                                onChange={(event: SelectChangeEvent) => {
                                                                    if (value.content?.phraseSoundGroup) {
                                                                        const phraseSoundGroup = {
                                                                            ...value.content?.phraseSoundGroup,
                                                                            ...(value.content?.phraseSoundGroup?.jyutping && {
                                                                                jyutping: [{ startIndex: parseInt(event.target.value) }],
                                                                            }),
                                                                        };

                                                                        const updatedContent = {
                                                                            ...value.content,
                                                                            phraseSoundGroup: phraseSoundGroup,
                                                                        };
                                                                        const updateModel: SlideComponentHkcsQ1 = { ...value, content: updatedContent };

                                                                        debouncedUpdate(updateModel);
                                                                    }
                                                                }}
                                                            >
                                                                {value.content?.phraseSound?.jyutping?.map((_phraseSound, index) => {
                                                                    return (
                                                                        <MenuItem value={index.toString()} key={index}>
                                                                            <Stack direction='row' spacing={1} alignItems={'center'}>
                                                                                <Pronunciation
                                                                                    phraseSound={value.content?.phraseSound}
                                                                                    phrase={value.content?.phrase}
                                                                                    groupIndex={index}
                                                                                    folder={'jyutping'}
                                                                                />
                                                                                <Typography>
                                                                                    {t('slide.legacy.character.phraseSoundGroup', {
                                                                                        i: index + 1,
                                                                                    })}
                                                                                </Typography>
                                                                            </Stack>
                                                                        </MenuItem>
                                                                    );
                                                                })}
                                                            </Select>
                                                        </FormControl>
                                                    </Box>
                                                )}
                                            </Paper>
                                            <Box sx={{ flex: 1, alignContent: 'center', mx: 5 }}>
                                                <PhraseHighlight phrase={value.content.phrase} highlight={value.content.phrase} underline={false} />
                                            </Box>
                                            <IconButton
                                                sx={{ position: 'absolute', top: 0, right: 0, m: 0.5 }}
                                                onClick={() => {
                                                    setWordPickerAnchorEl(wordSelectorRef.current);
                                                }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </>
                                    ) : (
                                        <Button
                                            variant='contained'
                                            onClick={() => {
                                                setWordPickerAnchorEl(wordSelectorRef.current);
                                            }}
                                            sx={{
                                                minWidth: '50%',
                                                bottom: 0,
                                                left: 0,
                                                right: 0,
                                                m: 1,
                                            }}
                                        >
                                            {t('slide.legacy.character.selectWord')}
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                            <Stack spacing={1} direction={'row'} sx={{ alignItems: 'center', width: '65%' }}>
                                <Box sx={{ width: '50%' }}>
                                    <ImageSelector
                                        src={value.content.image1URL ? value.content.image1URL : value.content.image1 ? `/asset/characters/${value.content.image1}/image.png` : undefined}
                                        onSelect={(_, selectorRef) => {
                                            setImagePickerSource('primary');
                                            setImagePickerAnchorEl(selectorRef.current);
                                        }}
                                    />
                                    <TextField
                                        placeholder={t('slide.legacy.hkcsQ1.label.associatedPhrase')}
                                        slotProps={{
                                            input: {
                                                inputProps: {
                                                    style: {
                                                        textAlign: 'center',
                                                    },
                                                },
                                            },
                                        }}
                                        fullWidth
                                        sx={{ mt: 1 }}
                                        defaultValue={value.content.keyword1}
                                        onChange={(e) => {
                                            debouncedUpdate({
                                                ...value,
                                                content: {
                                                    ...value.content,
                                                    keyword1: e.target.value,
                                                },
                                            });
                                        }}
                                    />
                                </Box>
                                <Box sx={{ width: '50%' }}>
                                    <ImageSelector
                                        src={value.content.image2URL ? value.content.image2URL : value.content.image2 ? `/asset/characters/${value.content.image2}/image.png` : undefined}
                                        onSelect={(_, selectorRef) => {
                                            setImagePickerSource('secondary');
                                            setImagePickerAnchorEl(selectorRef.current);
                                        }}
                                    />
                                    <TextField
                                        placeholder={t('slide.legacy.hkcsQ1.label.associatedPhrase')}
                                        slotProps={{
                                            input: {
                                                inputProps: {
                                                    style: {
                                                        textAlign: 'center',
                                                    },
                                                },
                                            },
                                        }}
                                        fullWidth
                                        sx={{ mt: 1 }}
                                        defaultValue={value.content.keyword2}
                                        onChange={(e) => {
                                            debouncedUpdate({
                                                ...value,
                                                content: {
                                                    ...value.content,
                                                    keyword2: e.target.value,
                                                },
                                            });
                                        }}
                                    />
                                </Box>
                            </Stack>
                        </Stack>
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                            <PhraseHighlight
                                phrase={t('slide.legacy.hkcsQ1.label.description', { phrase: value?.content?.phrase })}
                                highlight={value?.content?.phrase}
                                underline={false}
                                sx={{ fontSize: 24, color: 'text.secondary' }}
                                gutterBottom
                            />
                            <Box sx={{ position: 'relative', width: '100%', flex: 1, justifyItems: 'center' }}>
                                <Box sx={{ width: '60%', height: '100%', backgroundColor: 'white', borderRadius: 2, border: 2, borderColor: 'divider' }} />
                                <DoubleArrowIcon sx={{ position: 'absolute', right: 40, top: 0, bottom: 0, margin: 'auto 0', fontSize: 60, transform: 'scaleX(-1)' }} />
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ width: '30%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', borderRadius: 2, p: 1 }}>
                        <Stack spacing={1} sx={{ alignItems: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
                            {value.content.questions.map((question, index) => {
                                return (
                                    <Box key={`image-selector-${index}`} sx={{ display: 'flex', flex: 1, flexDirection: 'row', justifyContent: 'center' }}>
                                        <Box sx={{ flex: 0, aspectRatio: 1 }}>
                                            <ImageSelector
                                                src={question.imageURL ? question.imageURL : question.image ? `/asset/characters/${question.image}/image.png` : undefined}
                                                onSelect={(_, selectorRef) => {
                                                    setImagePickerSource(index);
                                                    setImagePickerAnchorEl(selectorRef.current);
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Box>
                </Box>
            </Popover>

            {imagePickerAnchorEl && (
                <ImagePicker
                    type='image'
                    title={t('slide.legacy.character.title')}
                    librarySources={BUILTIN_IMG_SRC}
                    anchorEl={imagePickerAnchorEl}
                    slotProps={{
                        paper: {
                            ref: imagePickerRef, // Attach the ref to the Paper element
                        },
                    }}
                    value={{
                        value: imagePickerSource === 'primary' ? value.content.image1 : imagePickerSource === 'secondary' ? value.content.image2 : '',
                        imageURL: imagePickerSource === 'primary' ? value.content.image1URL : imagePickerSource === 'secondary' ? value.content.image2URL : '',
                    }}
                    onClose={() => {
                        setImagePickerAnchorEl(null);
                    }}
                    onUpdate={(settings) => {
                        const updatedContent: SlideComponentHkcsQ1['content'] = {
                            ...value.content,
                            image1: imagePickerSource === 'primary' ? settings.value : value.content.image1,
                            image1URL: imagePickerSource === 'primary' ? settings.imageURL : value.content.image1URL,
                            image2: imagePickerSource === 'secondary' ? settings.value : value.content.image2,
                            image2URL: imagePickerSource === 'secondary' ? settings.imageURL : value.content.image2URL,
                            questions: value.content.questions.map((question, index) => {
                                if (index === imagePickerSource) {
                                    return {
                                        ...question,
                                        image: settings.value,
                                        imageURL: settings.imageURL,
                                    };
                                }
                                return question;
                            }),
                        };
                        const updateModel = { ...value, content: updatedContent, ...(settings.size && imagePickerSource === 'primary' ? { size: settings.size } : { size: value.size }) };

                        debouncedUpdate(updateModel);
                    }}
                    onUpload={onUploadImage}
                />
            )}

            {wordPickerAnchorEl && (
                <WordPicker
                    type='phrase'
                    title={t('slide.legacy.character.characterSearch')}
                    anchorEl={wordPickerAnchorEl}
                    slotProps={{
                        paper: {
                            ref: wordPickerRef, // Attach the ref to the Paper element
                        },
                    }}
                    value={{
                        characterList: value?.content?.characterList,
                        characterSound: value?.content?.characterSound,
                        phrase: value?.content?.phrase,
                        phraseSound: value?.content?.phraseSound,
                        phraseSoundGroup: value?.content?.phraseSoundGroup,
                    }}
                    onClose={() => {
                        setWordPickerAnchorEl(null);
                    }}
                    onUpdate={(settings) => {
                        const updatedContent: SlideComponentHkcsQ1['content'] = {
                            ...value.content,
                            character: 0, // reset character index
                            characterList: settings.characterList ?? [],
                            characterSound: settings.characterSound ?? { pinyin: [], jyutping: [] },
                            phrase: settings.phrase ?? '',
                            phraseSound: settings.phraseSound ?? { pinyin: [], jyutping: [] },
                            phraseSoundGroup: settings.phraseSoundGroup ?? { pinyin: [], jyutping: [] },
                        };
                        const updateModel: SlideComponentHkcsQ1 = { ...value, content: updatedContent };

                        debouncedUpdate(updateModel);
                    }}
                />
            )}
        </>
    );
};
